/**
 * 後臺深色主題的 design token（v2.32.0）
 *
 * 這份的原始出處是 `views/tiny/TinyClubConfig.js`（俱樂部管理頁）——
 * 本專案新頁面的視覺基準。抽出來是因為這個 repo 有明確的重複實作病史：
 * `getWeekExpenses`（表格與圖表各抄一份）、`CashflowEditCell`
 * （TinyCashflow 與 TinyMiscFee 各一份）、下載檔案（三個檔各抄一份）——
 * 每一次都是「要改的時候才發現有兩份」。
 *
 * ⚠️ `TinyClubConfig.js` 目前**仍保有自己那份**（一模一樣的值）。
 * 沒有一併改掉是因為那支 1,400 行、正在服役，為了 de-dup 去動它的
 * 回歸風險大於收益。要收斂是獨立的一題：把 TinyClubConfig 改成 import
 * 這裡，跑一次視覺回歸即可。**在那之前請不要再抄第三份。**
 */

export const COLORS = {
  cardBg: '#1e2530',
  headerBg: '#252d3a',
  inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)',
  borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff',
  textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)',
  textDimmed: 'rgba(255,255,255,0.4)',
  accent: '#667eea',
  // 導覽（頁面 tab 底線、標題 icon、週期選擇器的日曆 icon）的 active 色。
  //
  // **刻意跟 accent 分開**：accent 全站按鈕 / 卡片 / focus 都在用，
  // 為了換導覽顏色去動它會牽動整站。改導覽只要改這一個。
  //
  // 亮青 #11cdef —— 取自主題的 info-light，Brad 於 2026-09-10 從六個
  // 候選（全部是專案既有的顏色）中選定。
  navActive: '#11cdef',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  dangerGradient: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
  success: '#2dce89',
  danger: '#f5365c',
  info: '#11cdef',
  warning: '#fb6340',
};

export const inputStyle = {
  background: COLORS.inputBg,
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.borderInput}`,
  borderRadius: '6px',
};

export const labelStyle = {
  color: COLORS.textSecondary,
  fontWeight: 500,
  fontSize: '13px',
};

export const modalHeaderStyle = {
  background: COLORS.headerBg,
  color: COLORS.textPrimary,
  borderBottom: `1px solid ${COLORS.border}`,
};

export const modalBodyStyle = {
  background: COLORS.cardBg,
  color: COLORS.textPrimary,
};

export const modalFooterStyle = {
  background: COLORS.headerBg,
  borderTop: `1px solid ${COLORS.border}`,
};

export const cancelBtnStyle = {
  background: 'rgba(149, 165, 166, 0.2)',
  border: '1px solid rgba(149, 165, 166, 0.5)',
  color: '#95a5a6',
  padding: '8px 20px',
  borderRadius: '6px',
};

export const saveBtnStyle = (disabled) => ({
  background: disabled ? 'rgba(102, 126, 234, 0.5)' : COLORS.accentGradient,
  border: 'none',
  padding: '8px 30px',
  borderRadius: '6px',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
});

export const thStyle = {
  borderColor: COLORS.border,
  padding: '10px 8px',
  fontWeight: 600,
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

export const tdStyle = {
  borderColor: 'rgba(255,255,255,0.05)',
  padding: '10px 8px',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

/* ==========================================================================
   軟色調徽章 + 開關（v2.32.0）

   ⚠️ **不要用 reactstrap 的 `<Badge color="success">` 這種實心徽章。**
   它是 Bootstrap 亮色主題的預設值：高飽和填色 + 白字。放在這個深色後臺上
   兩個顏色會互相震動，看久了眼睛很不舒服（實際回報過）。

   正確做法照 `TinyClubConfig.js` 的 `RATE_TYPE_COLORS`：
       背景 = 該色 15% 透明   邊框 = 該色 40% 透明   文字 = 該色原色
   深色底 + 同色系文字，對比夠、但不刺眼。
   ========================================================================== */

/**
 * 色值 → 帶透明度的 rgba。
 *
 * ⚠️ COLORS 裡**不是每個值都是 hex** —— `textMuted` / `textDimmed` 本身就是
 * `rgba(255,255,255,0.5)` 這種。直接對它做 hex 解析會得到 NaN，
 * 徽章就變成看不見的透明塊（而且不會報錯，又是一個靜默失敗）。
 * 所以非 hex 一律退回中性白色調。
 */
function hexToRgba(color, alpha) {
  const m = /^#([0-9a-f]{6})$/i.exec((color || '').trim());
  if (!m) return `rgba(255, 255, 255, ${alpha})`;
  const h = m[1];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * 軟色調徽章樣式。
 * @param {string} color  COLORS 裡的其中一個（或任何 #rrggbb）
 * @param {object} extra  額外覆寫
 */
export function softBadge(color, extra = {}) {
  return {
    background: hexToRgba(color, 0.15),
    border: `1px solid ${hexToRgba(color, 0.4)}`,
    color,
    padding: '3px 9px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '5px',
    letterSpacing: '0.2px',
    ...extra,
  };
}

/** 中性 / 停用狀態用的灰徽章（沒有主色可用時）。 */
export const mutedBadge = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.14)',
  color: COLORS.textMuted,
  padding: '3px 9px',
  fontSize: '11px',
  fontWeight: 600,
  borderRadius: '5px',
};

/**
 * 深色主題用的開關樣式。
 *
 * ⚠️ 不用 reactstrap 的 `<CustomInput type="switch">` —— Bootstrap 的
 * custom-switch 在這個深色主題下、尤其是**開啟狀態**顯示得很怪
 * （實際回報過）。與其跟主題的 CSS 對打，不如自己畫一個小的。
 */
export const switchStyles = {
  track: (on, color) => ({
    position: 'relative',
    display: 'inline-block',
    width: 42,
    height: 22,
    borderRadius: 999,
    cursor: 'pointer',
    verticalAlign: 'middle',
    transition: 'background 0.18s ease, border-color 0.18s ease',
    background: on ? hexToRgba(color, 0.32) : 'rgba(255,255,255,0.07)',
    border: `1px solid ${on ? hexToRgba(color, 0.75) : 'rgba(255,255,255,0.18)'}`,
  }),
  knob: (on, color) => ({
    position: 'absolute',
    top: 2,
    left: on ? 22 : 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    transition: 'left 0.18s ease, background 0.18s ease',
    background: on ? color : 'rgba(255,255,255,0.45)',
    boxShadow: on ? `0 0 6px ${hexToRgba(color, 0.6)}` : 'none',
  }),
  disabled: { opacity: 0.35, cursor: 'not-allowed' },
};

export { hexToRgba };
